import React, { useCallback, useMemo, useState } from 'react'
import { Button, Grid, Icon } from 'gu-ui-kit'
import { useTranslation } from 'react-i18next'
import styles from 'components/FormControls/form-controls.module.scss'
import DatePicker from 'components/DatePicker'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { useDispatch } from 'react-redux'
import { MODIFY_DATES } from 'modules/Services/CampaignsEditor/subcomponents/BudgetEditor/types'
import format from 'utils/dateFns/format'
import { addMonths } from 'date-fns'

const DatesControls = ({ resource, touched, disabled, submitting, error }) => {
  const isAdGroups = useMemo(() => resource === 'ad-groups', [resource])
  const breakpoints = useBreakpoint()
  const { t } = useTranslation()
  const { md } = useBreakpoint()
  const sm = !md
  const [startDate, setStartDate] = useState(isAdGroups ? null : new Date())
  const [endDate, setEndDate] = useState(null)
  const [isPristine, setIsPristine] = useState(true)
  const dispatch = useDispatch()

  const modifyRows = useCallback(() => {
    dispatch({
      type: MODIFY_DATES,
      payload: {
        resource,
        date_start: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
        date_end: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
      },
    })
    setIsPristine(true)
  }, [startDate, endDate])

  return (
    <Grid
      gap={16}
      wrap={sm}
      className={clsx('margin-bottom-32', {
        [styles['fullWith']]: sm,
      })}
    >
      <DatePicker
        fullWidth={sm}
        InputProps={{
          size: 'm',
        }}
        mode={'calendar'}
        minDate={isAdGroups ? addMonths(new Date(), -30) : new Date()}
        maxDate={endDate ?? undefined}
        label={
          startDate
            ? ''
            : 'campaigns_editor::budget_editor.controls_panel.dates.date_start.placeholder'
        }
        placeholder={t(
          'campaigns_editor::budget_editor.controls_panel.dates.date_start.placeholder',
        )}
        value={startDate}
        onChange={(newVal) => {
          setStartDate(newVal)
          setIsPristine(false)
        }}
        touched={touched}
        disabled={disabled || submitting}
        error={touched && error ? error : null}
        buttonClassName={styles['date-picker__button']}
        asButton={false}
        popoverConfig={{
          variant: 'popover',
          id: 'datePickerPopover',
          breakpoints: {
            0: {
              variant: 'bottomSheet',
              hasBackdrop: true,
              showLabel: true,
              label: t(
                'campaigns_editor::budget_editor.controls_panel.dates.date_start.placeholder',
              ),
            },
            768: {
              variant: 'popover',
              hasBackdrop: false,
            },
          },
        }}
      />
      <DatePicker
        fullWidth={sm}
        InputProps={{
          size: 'm',
        }}
        mode={'calendar'}
        minDate={startDate ?? undefined}
        label={
          endDate ? '' : 'campaigns_editor::budget_editor.controls_panel.dates.date_end.placeholder'
        }
        value={endDate}
        onChange={(newVal) => {
          setEndDate(newVal)
          setIsPristine(false)
        }}
        touched={touched}
        disabled={disabled || submitting}
        error={touched && error ? error : null}
        buttonClassName={styles['date-picker__button']}
        asButton={false}
        popoverConfig={{
          variant: 'popover',
          id: 'datePickerPopover',
          breakpoints: {
            0: {
              variant: 'bottomSheet',
              hasBackdrop: true,
              showLabel: true,
              label: t('campaigns_editor::budget_editor.controls_panel.dates.date_end.placeholder'),
            },
            768: {
              variant: 'popover',
              hasBackdrop: false,
            },
          },
        }}
      />
      <Button
        noLabel={!breakpoints.fb && breakpoints.md}
        fullWidth={sm}
        size={'m'}
        disabled={isPristine}
        variant={'secondary'}
        startIcon={<Icon name={'check'} />}
        onClick={modifyRows}
      >
        {t('common::buttons.apply')}
      </Button>
    </Grid>
  )
}

export default DatesControls

DatesControls.propTypes = {
  resource: PropTypes.string,
  onChange: PropTypes.func,
  touched: PropTypes.bool,
  disabled: PropTypes.bool,
  submitting: PropTypes.bool,
  error: PropTypes.string,
}
